import { ref, readonly } from "vue";
import { defineStore } from "pinia";
import { makeApi } from "~/api";
import { sub } from "date-fns";
import socketService from "~/services/socketService";
import { auctionApi } from "~/api/auction";

const api = makeApi(`${import.meta.env.VITE_API_URL}/pol-price`);

const useUsdEUroRate = defineStore("UsdEurRate", () => {
  const maxRateTimeoutS = 70;
  const maxRateTimeoutMs = maxRateTimeoutS * 1_000;

  const lastUpdateTime = ref<Date>(sub(new Date(), { seconds: maxRateTimeoutS }));
  const rates = ref<{
    id: number;
    usdToEur: number;
    fetchedAt: number;
  }>({
    id: 0,
    usdToEur: 0,
    fetchedAt: 0,
  });

  const refreshInterval = ref<NodeJS.Timeout | null>(null);
  const isInitialized = ref(false);

  async function refreshRates() {
    try {
      const resp = await auctionApi.rates.usdToEur.get.query();

      lastUpdateTime.value = new Date();
      rates.value = {
        id: resp.id,
        usdToEur: +resp.usdToEur,
        fetchedAt: +resp.fetchedAt,
      };
    } catch (error) {
      console.error("Error fetching USD to EURO rate:", error);
    }
  }

  // Initialize function - must be called manually
  async function initialize() {
    if (isInitialized.value) return;

    isInitialized.value = true;

    try {
      // Initial refresh
      await refreshRates();

      // Set up interval
      refreshInterval.value = setInterval(refreshRates, maxRateTimeoutMs);
    } catch (error) {
      console.error("Error initializing POL price store:", error);
      isInitialized.value = false; // Reset so it can be retried
    }
  }

  return {
    rates,
    lastUpdateTime,
    refreshRates,
    initialize,
    isInitialized: readonly(isInitialized), // Make it readonly for external access
  };
});

export default useUsdEUroRate;
